import React, { useEffect, useRef, useState } from "react";
import { M } from "./tokens";

/* ══════════════════════════════════════════════════════════════
   ONE REPORT · FIVE FORMATS
   The same Monday numbers, rendered as a PDF, a Slack message,
   an email, a dashboard and a slide. Re-skinned per brand to show
   it arrives the way THEIR brand wants it.
   ══════════════════════════════════════════════════════════════ */

const BRANDS = [
  { id: "axos", name: "Axos", accent: "#5FE3A1", domain: "ibdassist.com" },
  { id: "halo", name: "Halo Coffee", accent: "#FFC46B", domain: "halocoffee.com" },
  { id: "loba", name: "Loba Skin", accent: "#FF8FA3", domain: "lobaskin.com" },
];

const FORMATS = [
  { id: "pdf", label: "PDF" },
  { id: "slack", label: "Slack" },
  { id: "email", label: "Email" },
  { id: "dash", label: "Dashboard" },
  { id: "slides", label: "Slides" },
];

const R = {
  week: "Week 23 · Monday 06:00",
  metrics: [
    ["Revenue", "$248,100", "+6.2%"],
    ["Blended CAC", "$54.20", "watch"],
    ["CVR · PDP", "3.42%", "+3.4%"],
    ["Email share", "28.4%", "+1.9%"],
  ],
  shipped: [
    "Ad #012 scaled, budget tripled",
    "Page test won and shipped, +3.4% orders",
    "Winback subject line called: 48.9% opens",
  ],
  needsYou: "2 calls need you: $6k spend raise, repeat buyer dip",
  bars: [42, 58, 47, 66, 71, 55, 78],
};

const Mark = ({ b, dark }) => (
  <span className="flex items-center gap-1.5">
    <span className="inline-block h-3 w-3 rounded-[3px]" style={{ background: b.accent }} />
    <span className="f-grotesk text-[11px] font-bold tracking-[-0.01em]" style={{ color: dark ? "#1B1A17" : M.ink }}>
      {b.name}
    </span>
  </span>
);

/* ── paper one pager ── */
function PdfDoc({ b }) {
  return (
    <div className="mx-auto w-full max-w-[400px] rounded-lg p-5" style={{ background: "#FDFBF7", color: "#1B1A17", boxShadow: "0 24px 60px rgba(0,0,0,0.55)" }}>
      <div className="flex items-center justify-between border-b pb-3" style={{ borderColor: "#E7E2D6" }}>
        <Mark b={b} dark />
        <span className="f-mono text-[8px] uppercase tracking-[0.16em]" style={{ color: "#8A857A" }}>Weekly report</span>
      </div>
      <div className="f-mono mt-3 text-[8.5px] uppercase tracking-[0.14em]" style={{ color: b.accent, filter: "brightness(0.72)" }}>{R.week}</div>
      <div className="mt-2 grid grid-cols-2 gap-2">
        {R.metrics.map(([l, v, d]) => (
          <div key={l} className="rounded border p-2.5" style={{ borderColor: "#E7E2D6" }}>
            <div className="f-mono text-[7.5px] uppercase tracking-[0.12em]" style={{ color: "#8A857A" }}>{l}</div>
            <div className="f-grotesk tnum mt-0.5 text-[17px] font-bold leading-none">{v}</div>
            <div className="f-mono mt-1 text-[8px]" style={{ color: d === "watch" ? "#B98A2C" : "#3E8E5F" }}>{d}</div>
          </div>
        ))}
      </div>
      <div className="f-mono mt-3 text-[7.5px] uppercase tracking-[0.14em]" style={{ color: "#8A857A" }}>What shipped</div>
      {R.shipped.map((s) => (
        <div key={s} className="mt-1.5 flex items-start gap-2 text-[10.5px] leading-snug">
          <span className="mt-[5px] h-1 w-1 shrink-0 rounded-full" style={{ background: b.accent, filter: "brightness(0.8)" }} />
          {s}
        </div>
      ))}
      <div className="mt-3 flex items-center justify-between border-t pt-2.5" style={{ borderColor: "#E7E2D6" }}>
        <span className="f-mono text-[8px]" style={{ color: "#B98A2C" }}>{R.needsYou}</span>
        <span className="f-mono text-[7.5px]" style={{ color: "#8A857A" }}>1 / 3</span>
      </div>
    </div>
  );
}

/* ── slack thread ── */
function SlackMsg({ b }) {
  return (
    <div className="mx-auto w-full max-w-[440px] overflow-hidden rounded-lg border" style={{ background: "#1A1D21", borderColor: "#2C2F33" }}>
      <div className="flex items-center gap-2 border-b px-4 py-2.5" style={{ borderColor: "#2C2F33" }}>
        <span className="f-grotesk text-[12px] font-bold" style={{ color: "#E8E8E8" }}># growth-weekly</span>
        <span className="f-mono ml-auto text-[8px] uppercase tracking-[0.1em]" style={{ color: "#8D9196" }}>Mon 6:00 AM</span>
      </div>
      <div className="flex gap-3 px-4 py-3.5">
        <span className="f-grotesk flex h-8 w-8 shrink-0 items-center justify-center rounded text-[12px] font-bold" style={{ background: b.accent, color: "#101214" }}>
          {b.name[0]}
        </span>
        <div className="min-w-0">
          <div className="flex items-baseline gap-2">
            <span className="f-grotesk text-[12.5px] font-bold" style={{ color: "#E8E8E8" }}>{b.name} Agent</span>
            <span className="f-mono rounded-sm px-1 py-px text-[7px] uppercase" style={{ background: "#2C2F33", color: "#8D9196" }}>app</span>
          </div>
          <div className="mt-1 text-[12px] font-semibold leading-snug" style={{ color: "#E8E8E8" }}>
            Week 23: $248,100 revenue, +6.2%. Everything below already handled.
          </div>
          {R.shipped.map((s) => (
            <div key={s} className="mt-1.5 flex items-start gap-2 text-[11px] leading-snug" style={{ color: "#B8BCC1" }}>
              <span className="mt-[5px] h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: b.accent }} />
              {s}
            </div>
          ))}
          <div className="mt-2 text-[11px]" style={{ color: "#E5B65C" }}>{R.needsYou}</div>
          <div className="f-mono mt-2.5 text-[9px]" style={{ color: b.accent }}>12 replies · view thread</div>
        </div>
      </div>
    </div>
  );
}

/* ── email digest ── */
function EmailMsg({ b }) {
  return (
    <div className="mx-auto w-full max-w-[440px] overflow-hidden rounded-lg" style={{ background: "#FDFBF7", color: "#1B1A17", boxShadow: "0 24px 60px rgba(0,0,0,0.55)" }}>
      <div className="border-b px-4 py-3" style={{ borderColor: "#E7E2D6" }}>
        <div className="f-mono text-[8.5px]" style={{ color: "#8A857A" }}>
          From <span style={{ color: "#1B1A17" }}>{b.name} Weekly</span> · agent@{b.domain}
        </div>
        <div className="f-grotesk mt-1.5 text-[14px] font-bold leading-snug">
          Monday: $248,100, +6.2%. Two calls need you.
        </div>
      </div>
      <div className="px-4 py-3.5">
        <div className="grid grid-cols-4 gap-1.5">
          {R.metrics.map(([l, v]) => (
            <div key={l} className="rounded p-1.5 text-center" style={{ background: "#F3EFE6" }}>
              <div className="f-grotesk tnum text-[12px] font-bold leading-none">{v}</div>
              <div className="f-mono mt-1 text-[6.5px] uppercase tracking-[0.08em]" style={{ color: "#8A857A" }}>{l}</div>
            </div>
          ))}
        </div>
        {R.shipped.slice(0, 2).map((s) => (
          <div key={s} className="mt-2 flex items-start gap-2 text-[10.5px] leading-snug">
            <span className="mt-[5px] h-1 w-1 shrink-0 rounded-full" style={{ background: b.accent, filter: "brightness(0.8)" }} />
            {s}
          </div>
        ))}
        <div className="f-mono mt-3.5 inline-block rounded px-3.5 py-2 text-[8.5px] font-semibold uppercase tracking-[0.12em]" style={{ background: b.accent, color: "#141311" }}>
          Open the full report
        </div>
      </div>
    </div>
  );
}

/* ── live dashboard ── */
function Dash({ b }) {
  return (
    <div className="mx-auto w-full max-w-[460px] rounded-lg border p-4" style={{ borderColor: M.border, background: M.surface2 }}>
      <div className="flex items-center justify-between">
        <Mark b={b} />
        <span className="f-mono flex items-center gap-1.5 text-[8px] uppercase tracking-[0.12em]" style={{ color: b.accent }}>
          <span className="h-1.5 w-1.5 rounded-full" style={{ background: b.accent, boxShadow: `0 0 8px ${b.accent}` }} />
          live
        </span>
      </div>
      <div className="mt-3 grid grid-cols-4 gap-1.5">
        {R.metrics.map(([l, v, d]) => (
          <div key={l} className="rounded border p-2" style={{ borderColor: M.border, background: M.surface }}>
            <div className="f-mono text-[6.5px] uppercase tracking-[0.08em]" style={{ color: M.dim }}>{l}</div>
            <div className="f-grotesk tnum mt-1 text-[13px] font-bold leading-none">{v}</div>
            <div className="f-mono mt-1 text-[7.5px]" style={{ color: d === "watch" ? M.warn : b.accent }}>{d}</div>
          </div>
        ))}
      </div>
      <div className="mt-2.5 flex h-[64px] items-end gap-1.5 rounded border p-2" style={{ borderColor: M.border, background: M.surface }}>
        {R.bars.map((h, i) => (
          <div key={i} className="flex-1 rounded-sm" style={{ height: `${h}%`, background: i === R.bars.length - 1 ? b.accent : `${b.accent}44` }} />
        ))}
      </div>
      <div className="f-mono mt-2 flex justify-between text-[8px] uppercase tracking-[0.1em]" style={{ color: M.dim }}>
        <span>47 actions on the record</span>
        <span style={{ color: M.warn }}>2 waiting on you</span>
      </div>
    </div>
  );
}

/* ── one slide of the deck ── */
function Slides({ b }) {
  return (
    <div className="mx-auto w-full max-w-[460px]">
      <div className="relative overflow-hidden rounded-lg border" style={{ borderColor: M.border, background: "#101214", aspectRatio: "16 / 9", boxShadow: "0 24px 60px rgba(0,0,0,0.55)" }}>
        <div className="absolute inset-x-0 top-0 h-1" style={{ background: b.accent }} />
        <div className="flex h-full flex-col justify-between p-5">
          <div className="f-mono text-[8px] uppercase tracking-[0.18em]" style={{ color: M.dim }}>{R.week}</div>
          <div>
            <div className="f-grotesk tnum text-[44px] font-bold leading-none" style={{ color: b.accent }}>+6.2%</div>
            <div className="f-grotesk mt-1.5 text-[15px] font-semibold" style={{ color: M.ink }}>Revenue, week over week. Here is what shipped.</div>
          </div>
          <div className="flex items-center justify-between">
            <Mark b={b} />
            <span className="f-mono text-[8px]" style={{ color: M.dim }}>03 / 12</span>
          </div>
        </div>
      </div>
      <div className="mt-2 flex justify-center gap-1.5">
        {[0, 1, 2, 3].map((i) => (
          <span key={i} className="h-1 w-4 rounded-full" style={{ background: i === 2 ? b.accent : M.border }} />
        ))}
      </div>
    </div>
  );
}

const CANVAS = { pdf: PdfDoc, slack: SlackMsg, email: EmailMsg, dash: Dash, slides: Slides };

export default function ReportFormats({ accent }) {
  const [fmt, setFmt] = useState(0);
  const [brand, setBrand] = useState(0);
  const touched = useRef(false);

  /* auto-morph between formats until the visitor takes over */
  useEffect(() => {
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    const t = setInterval(() => {
      if (!touched.current) setFmt((f) => (f + 1) % FORMATS.length);
    }, 3400);
    return () => clearInterval(t);
  }, []);

  const b = BRANDS[brand];
  const Canvas = CANVAS[FORMATS[fmt].id];

  return (
    <div data-testid="panel-formats">
      <div className="flex flex-wrap items-center gap-2 border-b px-4 py-2.5" style={{ borderColor: M.border, background: M.surface2 }}>
        <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: accent, boxShadow: `0 0 8px ${accent}` }} />
        <span className="f-mono text-[8.5px] uppercase tracking-[0.14em]" style={{ color: M.dim }}>Same report · their format</span>
        <div className="ml-auto flex gap-1">
          {BRANDS.map((br, i) => (
            <button
              key={br.id}
              onClick={() => { touched.current = true; setBrand(i); }}
              data-testid={`brand-chip-${br.id}`}
              className="focus-ring f-mono flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[8.5px] uppercase tracking-[0.08em] transition-colors"
              style={{
                color: i === brand ? br.accent : M.dim,
                border: `1px solid ${i === brand ? `${br.accent}66` : M.border}`,
                background: i === brand ? `${br.accent}12` : "transparent",
              }}
            >
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: br.accent }} />
              {br.name}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5 px-4 pt-3.5" data-testid="format-tabs">
        {FORMATS.map((f, i) => (
          <button
            key={f.id}
            onClick={() => { touched.current = true; setFmt(i); }}
            data-testid={`format-tab-${f.id}`}
            className="focus-ring f-mono rounded px-3 py-1.5 text-[9px] uppercase tracking-[0.12em] transition-colors"
            style={{
              color: i === fmt ? "#0A0C10" : M.muted,
              background: i === fmt ? accent : M.surface2,
              border: `1px solid ${i === fmt ? accent : M.border}`,
            }}
            aria-pressed={i === fmt}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="px-4 py-5" data-testid={`format-canvas-${FORMATS[fmt].id}`}>
        <div key={`${fmt}-${brand}`} style={{ animation: "stageIn 460ms cubic-bezier(0.16,1,0.3,1) both" }}>
          <Canvas b={b} />
        </div>
      </div>

      <div className="f-mono border-t px-4 py-2.5 text-[8.5px] uppercase tracking-[0.12em]" style={{ borderColor: M.border, color: M.dim }}>
        Same numbers underneath · your logo, your colors, your channel
      </div>
    </div>
  );
}
